export type PerformanceMode = 'eco' | 'rich';

const PERF_KEY = 'vyko_perf_mode';

/**
 * Detects whether the current device is likely a low-end device
 * based on CPU cores, RAM, data saver, or reduced motion settings.
 */
export function isLowSpecDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  // Check hardware concurrency (<= 4 cores indicates budget/entry device)
  if (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4) {
    return true;
  }

  // Check device memory (<= 4GB RAM)
  const nav = navigator as any;
  if (nav.deviceMemory && nav.deviceMemory <= 4) {
    return true;
  }

  // Check connection save-data or slow network
  if (nav.connection?.saveData || (nav.connection?.effectiveType && ['slow-2g', '2g', '3g'].includes(nav.connection.effectiveType))) {
    return true;
  }

  // Check user preference for reduced motion
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return true;
  }

  return false;
}

export function getPreferredPerformanceMode(): PerformanceMode {
  const saved = localStorage.getItem(PERF_KEY);
  if (saved === 'rich') {
    return 'rich';
  }

  // Default to eco mode
  return 'eco';
}

export function applyPerformanceMode(mode: PerformanceMode): void {
  document.documentElement.setAttribute('data-perf', mode);
  localStorage.setItem(PERF_KEY, mode);
  window.dispatchEvent(new CustomEvent('vyko-perf-change', { detail: { mode } }));
}

export function togglePerformanceMode(): PerformanceMode {
  const current = (document.documentElement.getAttribute('data-perf') as PerformanceMode) || getPreferredPerformanceMode();
  const next: PerformanceMode = current === 'eco' ? 'rich' : 'eco';
  applyPerformanceMode(next);
  return next;
}

export function initPerformanceMode(): PerformanceMode {
  // Clear legacy auto-saved 'rich' mode from previous session so eco mode is default
  if (localStorage.getItem('vyko_perf_v') !== '2') {
    localStorage.removeItem(PERF_KEY);
    localStorage.setItem('vyko_perf_v', '2');
  }

  const mode = getPreferredPerformanceMode();
  applyPerformanceMode(mode);
  return mode;
}

export function isEcoMode(): boolean {
  const current = document.documentElement.getAttribute('data-perf');
  return current === 'eco';
}
